import React, { type ReactNode } from 'react';
import {
  EMPTY_STATE_ALIGN_CENTER,
  EMPTY_STATE_SIZE_MD,
  type EmptyStateAlign,
  type EmptyStateSize,
} from './EmptyState.constants';
import {
  Actions,
  Description,
  IconSlot,
  Root,
  Title,
} from './EmptyState.styled';

export interface EmptyStateProps {
  /**
   * Decorative leading glyph — Material symbol span, react-icons element, or
   * raw svg. Rendered at the icon size for the active {@link size} token.
   * Omit when the empty state is purely textual.
   */
  icon?: ReactNode;
  /**
   * The headline — the only required content. Pass a string for the common
   * case (`"No runs in history."`) or a richer node for emphasised copy.
   */
  title: ReactNode;
  /**
   * Sub-text below the title, typically a one-liner explaining what action
   * the user can take next. Capped at 48ch so it stays readable on wide
   * surfaces.
   */
  description?: ReactNode;
  /**
   * Slot for the recovery action(s) — usually one or two `Button`s. Stacked
   * horizontally with an 8px gap and a small top margin from the description.
   */
  actions?: ReactNode;
  /** Stack alignment. Defaults to centered (full-bleed empty surface). */
  align?: EmptyStateAlign;
  /** Vertical scale. Defaults to `md` (page-level empty surface). */
  size?: EmptyStateSize;
  className?: string;
  dataTestId?: string;
}

/**
 * The "nothing-here-yet" placeholder pattern — icon + title + optional
 * description + optional action slot. Theme-agnostic: every colour falls
 * back to a sensible literal so it renders against any consumer theme.
 *
 * Use this whenever a list, table, or panel has no content to show. The
 * `actions` slot is intentionally a free-form ReactNode — pass any composition
 * of buttons; the atom doesn't care which `Button` variant you use, so it
 * stays decoupled from the action surface.
 *
 * @example
 *   <EmptyState
 *     icon={<MdHistoryToggleOff />}
 *     title="No runs in history"
 *     description="Test runs and live runs will appear here once you publish."
 *     actions={<Button variant="filled">Publish</Button>}
 *   />
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actions,
  align = EMPTY_STATE_ALIGN_CENTER,
  size = EMPTY_STATE_SIZE_MD,
  className,
  dataTestId = 'empty-state',
}) => {
  return (
    <Root
      $align={align}
      $size={size}
      className={className}
      data-testid={dataTestId}
      data-align={align}
      data-size={size}
    >
      {icon && (
        <IconSlot
          $size={size}
          aria-hidden="true"
          data-testid={`${dataTestId}__icon`}
        >
          {icon}
        </IconSlot>
      )}
      <Title $size={size} data-testid={`${dataTestId}__title`}>
        {title}
      </Title>
      {description && (
        <Description
          $size={size}
          data-testid={`${dataTestId}__description`}
        >
          {description}
        </Description>
      )}
      {actions && (
        <Actions data-testid={`${dataTestId}__actions`}>{actions}</Actions>
      )}
    </Root>
  );
};

export default EmptyState;
